import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { CLIENT_ROOT } from 'src/constants/paths';
import useTabs from 'src/hooks/useTabs';

import LeadResourceCallForm from '../LeadResourceCallForm';
import styles from './LeadWorkModal.module.scss';

const CREATE_PATIENT_TASK_KEY = 'create_patient_from_lead_manual';

const LeadWorkModal = ({
  task,
  relatedTasks = [],
  isClaimedByYou,
  onClose,
  onLeadUpdated,
  onSendConsent,
  isSendingConsent,
  onDisqualify,
  isDisqualifying,
  onCreatePatient,
  isCreatingPatient,
  onMarkComplete,
  isMarking,
}) => {
  const formRef = useRef(null);
  const backdropPressRef = useRef(false);
  const [formState, setFormState] = useState({
    canSave: false,
    isSaving: false,
    canCreatePatient: false,
  });
  const handleFormState = useCallback((next) => setFormState(next), []);
  // The patient chart opens as a platform tab, the way patient tasks open it, instead of
  // leaving the dashboard for a new browser tab.
  const { onSelectTab } = useTabs();

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!task) return null;

  const isCreatePatientTask = task.taskKey === CREATE_PATIENT_TASK_KEY;
  const lead = task.patient || {};
  const consentDispatch =
    task.metadata?.consent_dispatch ||
    relatedTasks.map((candidate) => candidate.metadata?.consent_dispatch).find(Boolean) ||
    null;
  const consentUrl = consentDispatch?.consent_url || null;
  const consentAlreadySent = !!(consentDispatch || lead.consentSentAt);
  const consentAccepted = lead.status === 'ENROLLED';
  const patientCreated = lead.patientId != null;
  const manualCreateTask = relatedTasks.find(
    (candidate) =>
      candidate.taskKey === CREATE_PATIENT_TASK_KEY &&
      ![3, 4, 5].includes(Number(candidate.status)) &&
      candidate.workflowReady,
  );
  const needsManualPatient =
    !patientCreated && (!!manualCreateTask || (isCreatePatientTask && !!task.workflowReady));
  const canCreatePatientHere = isCreatePatientTask && !!task.workflowReady;
  const outcomeSteps = [
    { key: 'sent', label: 'Consent sent', done: consentAlreadySent },
    { key: 'accepted', label: 'Consent accepted', done: consentAccepted },
    {
      key: 'patient',
      label: patientCreated
        ? `Patient #${lead.patientId} created`
        : needsManualPatient
          ? 'Patient needs manual creation'
          : 'Patient created',
      done: patientCreated,
    },
  ];
  const isBusy =
    formState.isSaving || isSendingConsent || isDisqualifying || isCreatingPatient || isMarking;
  const leadName = task.patient?.name || 'Lead';
  const createPatientTitle = (() => {
    if (!isClaimedByYou) return 'Claim this task before creating the patient';
    if (!consentAccepted) return 'The lead has not accepted the consent form yet';
    if (!task.workflowReady) return 'The workflow has not reached this step yet';
    if (!formState.canCreatePatient) return 'Fill in the highlighted fields and save first';
    return 'Create the patient record from this lead';
  })();

  const modalContent = (
    <div
      className={styles.modalOverlay}
      role="presentation"
      onMouseDown={(event) => {
        backdropPressRef.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget && backdropPressRef.current) onClose();
        backdropPressRef.current = false;
      }}
    >
      <div
        className={styles.modalCard}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-work-modal-title"
      >
        <div className={styles.modalHeader}>
          <div>
            <div id="lead-work-modal-title" className={styles.modalTitle}>
              {leadName}
            </div>
            <div className={styles.modalSubtitle}>
              {task.title || task.subject}
              {isClaimedByYou ? ' · claimed by you' : ''}
            </div>
          </div>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className={styles.outcomeStrip}>
          {outcomeSteps.map((step) => (
            <span
              key={step.key}
              className={`${styles.outcomeStep} ${step.done ? styles.outcomeStepDone : ''}`}
            >
              {step.done ? '✓' : '○'} {step.label}
            </span>
          ))}
          {consentUrl ? (
            <a
              className={styles.outcomeConsentLink}
              href={consentUrl}
              target="_blank"
              rel="noreferrer"
              title={consentUrl}
            >
              Consent form ↗
            </a>
          ) : null}
          {needsManualPatient && !isCreatePatientTask ? (
            <span className={styles.outcomeHint}>
              Finish it on the &ldquo;Create patient from lead manually&rdquo; step
            </span>
          ) : null}
          {!isCreatePatientTask && isClaimedByYou ? (
            <button
              type="button"
              className={styles.outcomeCompleteButton}
              disabled={isBusy || !patientCreated}
              title={patientCreated ? '' : 'Available once the patient has been created'}
              onClick={() => onMarkComplete?.(task)}
            >
              {isMarking ? 'Completing...' : 'Complete task'}
            </button>
          ) : null}
          {patientCreated ? (
            <button
              type="button"
              className={styles.outcomePatientLink}
              onClick={() => {
                onClose();
                onSelectTab({ path: `${CLIENT_ROOT}/patients/details/${lead.patientId}/info` });
              }}
            >
              Open patient chart ↗
            </button>
          ) : null}
        </div>
        <div className={styles.modalBody}>
          <LeadResourceCallForm
            ref={formRef}
            leadResourceId={task.leadResourceId}
            onLeadUpdated={onLeadUpdated}
            onActionStateChange={handleFormState}
            title="Lead details"
            onCreatePatient={
              canCreatePatientHere && isClaimedByYou && !patientCreated && consentAccepted
                ? () => onCreatePatient?.(task)
                : null
            }
            hideActions
          />
        </div>

        <div className={styles.modalFooter}>
          {patientCreated ? (
            <>
              <span className={styles.footerNote}>
                Patient created — this lead is closed. Changes here no longer affect the patient.
              </span>
              <span className={styles.footerSpacer} />
              <button type="button" className={styles.modalPrimaryAction} onClick={onClose}>
                Close
              </button>
            </>
          ) : isCreatePatientTask ? (
            <>
              <span className={styles.footerSpacer} />
              <button
                type="button"
                className={styles.modalSecondaryAction}
                disabled={isBusy || !formState.canSave}
                onClick={() => formRef.current?.save()}
              >
                {formState.isSaving ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                className={styles.modalPrimaryAction}
                disabled={
                  isBusy ||
                  !isClaimedByYou ||
                  !consentAccepted ||
                  !task.workflowReady ||
                  !formState.canCreatePatient
                }
                title={createPatientTitle}
                onClick={() => onCreatePatient?.(task)}
              >
                {isCreatingPatient ? 'Creating...' : 'Create patient'}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className={`${styles.modalSecondaryAction} ${styles.modalDangerAction}`}
                disabled={isBusy || !isClaimedByYou || consentAlreadySent}
                title={
                  consentAlreadySent
                    ? 'The consent form has been sent; the lead can no longer be disqualified here'
                    : ''
                }
                onClick={() => onDisqualify?.(task)}
              >
                {isDisqualifying ? 'Disqualifying...' : 'Disqualify lead'}
              </button>
              <span className={styles.footerSpacer} />
              <button
                type="button"
                className={styles.modalSecondaryAction}
                disabled={isBusy || !isClaimedByYou}
                title={isClaimedByYou ? '' : 'Claim this task before sending the consent form'}
                onClick={() => onSendConsent?.(task)}
              >
                {isSendingConsent
                  ? 'Sending...'
                  : consentAlreadySent
                    ? 'Resend consent form'
                    : 'Send consent form'}
              </button>
              <button
                type="button"
                className={styles.modalPrimaryAction}
                disabled={isBusy || !formState.canSave}
                onClick={() => formRef.current?.save()}
              >
                {formState.isSaving ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

LeadWorkModal.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.string,
    leadResourceId: PropTypes.string,
    title: PropTypes.string,
    subject: PropTypes.string,
    taskKey: PropTypes.string,
    metadata: PropTypes.object,
    patient: PropTypes.object,
    workflowReady: PropTypes.bool,
  }),
  relatedTasks: PropTypes.arrayOf(PropTypes.object),
  isClaimedByYou: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onLeadUpdated: PropTypes.func,
  onSendConsent: PropTypes.func,
  isSendingConsent: PropTypes.bool,
  onDisqualify: PropTypes.func,
  isDisqualifying: PropTypes.bool,
  onCreatePatient: PropTypes.func,
  isCreatingPatient: PropTypes.bool,
  onMarkComplete: PropTypes.func,
  isMarking: PropTypes.bool,
};

export default LeadWorkModal;
